import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowUpRight, EnvelopeSimple, ArrowLeft } from '@phosphor-icons/react';
import PRACTICE_AREAS from '../data/practiceAreas';
import TI from '../components/brand/TI';
import AgMark from '../components/brand/AgMark';
import useCanonicalUrl from '../hooks/useCanonicalUrl';

/* ====================================================================
 * PracticeDetailPage — shared editorial template used by the two
 * dedicated practice detail pages (Pharmaceuticals, Medical Devices).
 *
 * Keeps positioning language single-sourced from practiceAreas.js.
 * ==================================================================== */

const SLATE = '#0B1426';
const IVORY = '#F5F1E8';
const CORAL = '#FF4E6A';
const MUTED = '#5B6577';
const INK = '#1F2937';

export default function PracticeDetailPage({ practiceId, canonicalPath }) {
  useCanonicalUrl('tagc');
  const practice = PRACTICE_AREAS.find((p) => p.id === practiceId);
  if (!practice) return null;

  const contactSubject = encodeURIComponent(
    `${practice.title} — Consulting inquiry`
  );
  const contactBody = encodeURIComponent(
    `Briefly: company, product category, jurisdictions, timeline, and the scientific question that prompted the call.\n\n(Interest area: ${practice.title})`
  );

  return (
    <div className="min-h-screen font-sans" style={{ backgroundColor: IVORY, color: INK }}>
      <Helmet>
        <title>{`${practice.title} — The Agyekum Group`}</title>
        <meta
          name="description"
          content={`${practice.tagline} ${practice.body}`}
        />
        <link rel="canonical" href={`https://tagc.us${canonicalPath}`} />
        <meta property="og:title" content={`${practice.title} — The Agyekum Group`} />
        <meta property="og:description" content={practice.tagline} />
        <meta property="og:url" content={`https://tagc.us${canonicalPath}`} />
        <meta property="og:type" content="website" />
      </Helmet>

      {/* Header */}
      <header
        className="sticky top-0 z-50 border-b backdrop-blur-md"
        style={{ borderColor: '#E3D9C0', backgroundColor: 'rgba(245,241,232,0.92)' }}
      >
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group" data-testid="practice-page-mark">
            <AgMark size={32} variant="light" />
            <span className="font-serif text-lg sm:text-xl tracking-tight" style={{ color: SLATE }}>
              The Agyekum Group
            </span>
          </Link>
          <nav className="flex items-center gap-4 text-[12px] font-mono uppercase tracking-[0.22em]">
            <Link to="/#services" className="hover:opacity-70" style={{ color: SLATE }}>
              Practice
            </Link>
            <Link to="/#industries" className="hover:opacity-70" style={{ color: SLATE }}>
              Industries
            </Link>
            <Link to="/#contact" className="hover:opacity-70" style={{ color: SLATE }}>
              Contact
            </Link>
          </nav>
        </div>
      </header>

      {/* Breadcrumb + hero */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 pt-16 pb-8">
        <Link
          to="/#services"
          className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.25em] mb-8 hover:opacity-70"
          style={{ color: MUTED }}
          data-testid="practice-breadcrumb"
        >
          <ArrowLeft size={12} weight="bold" /> Back to practice
        </Link>

        <p
          className="text-[11px] font-mono uppercase tracking-[0.3em] mb-6"
          style={{ color: CORAL }}
        >
          Practice {practice.order} &middot; {practice.availability === 'expert-led' ? 'Expert-led consulting' : 'Available'}
        </p>
        <h1
          className="font-serif leading-[0.98] tracking-tight mb-6"
          style={{ color: SLATE, fontSize: 'clamp(2.25rem, 5.5vw, 4rem)' }}
          data-testid="practice-detail-headline"
        >
          {practice.title}
        </h1>
        <p className="text-lg lg:text-xl leading-relaxed max-w-3xl mb-4 italic" style={{ color: MUTED }}>
          {practice.tagline}
        </p>
        <p className="text-base leading-relaxed max-w-3xl" style={{ color: INK }}>
          {practice.body}
        </p>
      </section>

      {/* Hero image */}
      {practice.image && (
        <section
          className="relative w-full overflow-hidden border-t border-b"
          style={{ borderColor: '#E3D9C0' }}
          aria-hidden="true"
        >
          <img
            src={practice.image}
            alt={practice.imageAlt || ''}
            className="block w-full h-[42vh] min-h-[320px] max-h-[560px] object-cover select-none"
          />
        </section>
      )}

      {/* Positioning disclaimer (Medical Devices carries this) */}
      {practice.positioningDisclaimer && (
        <section
          className="border-b"
          style={{ borderColor: '#E3D9C0', backgroundColor: '#FBF7EE' }}
        >
          <div className="max-w-6xl mx-auto px-6 lg:px-10 py-10">
            <p
              className="text-[10px] font-mono uppercase tracking-[0.3em] mb-3"
              style={{ color: CORAL }}
              data-testid="practice-detail-scope-marker"
            >
              Scope of engagement
            </p>
            <p
              className="text-[15px] leading-relaxed max-w-4xl"
              style={{ color: INK }}
              data-testid="practice-detail-disclaimer"
            >
              {practice.positioningDisclaimer}
            </p>
          </div>
        </section>
      )}

      {/* Services list */}
      <section className="border-t" style={{ borderColor: '#E3D9C0' }}>
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-20 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <p
              className="text-[11px] font-mono uppercase tracking-[0.3em] mb-4"
              style={{ color: CORAL }}
            >
              Services
            </p>
            <h2 className="font-serif text-3xl lg:text-4xl leading-[1.05]" style={{ color: SLATE }}>
              What Aᴳ delivers.
            </h2>
          </div>
          <div className="lg:col-span-7 lg:col-start-6">
            <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-3 text-[15px]" style={{ color: INK }}>
              {practice.services.map((s) => (
                <li key={s} className="flex items-start gap-3 border-b pb-3" style={{ borderColor: '#E3D9C0' }}>
                  <span
                    className="mt-2 w-1.5 h-1.5 rounded-full shrink-0"
                    style={{ backgroundColor: CORAL }}
                  />
                  <span>{s}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Cross-links: industries · regulations · toxintel */}
      <section className="border-t" style={{ borderColor: '#E3D9C0', backgroundColor: '#FBF7EE' }}>
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-20 grid md:grid-cols-3 gap-10">
          <div>
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] mb-4" style={{ color: MUTED }}>
              Industries served
            </p>
            <ul className="space-y-1.5 text-[14px]" style={{ color: INK }}>
              {practice.industries.map((i) => (
                <li key={i}>&mdash; {i}</li>
              ))}
            </ul>
          </div>
          <div>
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] mb-4" style={{ color: MUTED }}>
              Applicable regulations
            </p>
            <ul className="space-y-1.5 text-[14px]" style={{ color: INK }}>
              {practice.regulations.map((r) => (
                <li key={r}>&mdash; {r}</li>
              ))}
            </ul>
          </div>
          <div>
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] mb-4" style={{ color: MUTED }}>
              Related <TI /> capability
            </p>
            <p className="text-[14px] leading-relaxed mb-4" style={{ color: INK }}>
              {practice.toxintelCapability}
            </p>
            <p className="text-[12.5px] leading-relaxed italic" style={{ color: MUTED }}>
              <TI /> is Aᴳ&rsquo;s enterprise scientific safety intelligence platform &mdash; a
              distinct product that supports, but does not replace, the consulting practice.
            </p>
          </div>
        </div>
      </section>

      {/* CTA band */}
      <section className="border-t" style={{ borderColor: '#1B2538', backgroundColor: SLATE, color: IVORY }}>
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-20 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <p className="text-[11px] font-mono uppercase tracking-[0.3em] mb-5" style={{ color: CORAL }}>
              Engage the practice
            </p>
            <h2
              className="font-serif leading-[1.02] tracking-tight mb-4"
              style={{ fontSize: 'clamp(1.75rem, 4vw, 2.75rem)' }}
            >
              Talk to an independent toxicologist about your {practice.title.toLowerCase()} question.
            </h2>
            <p className="text-base leading-relaxed opacity-90 max-w-2xl">
              Send a short brief &mdash; product, jurisdictions, timeline, and the scientific
              question that prompted the call. We respond within two business days.
            </p>
          </div>
          <div className="lg:col-span-4 lg:col-start-9 flex flex-col gap-3">
            <a
              href={`mailto:regulatory@tagc.us?subject=${contactSubject}&body=${contactBody}`}
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-sm text-[13px] font-medium uppercase tracking-wider transition-transform hover:-translate-y-px"
              style={{ backgroundColor: CORAL, color: SLATE }}
              data-testid="practice-detail-cta-contact"
            >
              <EnvelopeSimple size={14} weight="bold" /> Contact an expert
            </a>
            <a
              href={`mailto:regulatory@tagc.us?subject=${encodeURIComponent(
                `Request consultation — ${practice.title}`
              )}&body=${contactBody}`}
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-sm text-[13px] font-medium uppercase tracking-wider border transition-colors hover:bg-[#F5F1E8] hover:text-[#0B1426]"
              style={{ borderColor: IVORY, color: IVORY }}
              data-testid="practice-detail-cta-request"
            >
              Request consultation
            </a>
            <Link
              to="/#services"
              className="inline-flex items-center justify-center gap-1.5 mt-2 text-[12px] font-mono uppercase tracking-[0.22em] opacity-80 hover:opacity-100"
            >
              Back to practice <ArrowUpRight size={12} weight="bold" />
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t" style={{ borderColor: '#E3D9C0' }}>
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-10 flex flex-wrap items-center justify-between gap-4">
          <p className="text-[11px] font-mono uppercase tracking-[0.25em]" style={{ color: MUTED }}>
            © The Agyekum Group &middot; Independent scientific consulting
          </p>
          <Link
            to="/"
            className="text-[11px] font-mono uppercase tracking-[0.25em] hover:opacity-70"
            style={{ color: SLATE }}
          >
            tagc.us &rarr;
          </Link>
        </div>
      </footer>
    </div>
  );
}
